import { StyleProp, T } from '@tldraw/tldraw'
import { FontFamilyType, FontWeightType } from './Types'

const normalizedPercentage = (value: number) => {
  if (value > 1) throw new Error('Expected a positive number lower than 1')
}

export const TextColorStyle = StyleProp.define('blaze:textColor', {
  defaultValue: 'rgb(0, 0, 0)',
  type: T.string,
})
export type ITextColorStyle = T.TypeOf<typeof TextColorStyle>

export const FontSizeStyle = StyleProp.define('blaze:fontSize', {
  defaultValue: 16,
  type: T.positiveNumber,
})
export type IFontSizeStyle = T.TypeOf<typeof FontSizeStyle>

export const FontWeightStyle = StyleProp.define('blaze:fontWeight', {
  defaultValue: 400,
  type: T.positiveNumber,
}) as StyleProp<FontWeightType>
export type IFontWeightStyle = T.TypeOf<typeof FontWeightStyle>

export const FontFamilyStyle = StyleProp.define('blaze:fontFamily', {
  defaultValue: 'Cabin',
  type: T.string,
}) as StyleProp<FontFamilyType>
export type IFontFamilyStyle = T.TypeOf<typeof FontFamilyStyle>

export const ItalicStyle = StyleProp.define('blaze:italic', {
  defaultValue: false,
  type: T.boolean,
})
export type IItalicStyle = T.TypeOf<typeof ItalicStyle>

export const UnderlineStyle = StyleProp.define('blaze:underline', {
  defaultValue: false,
  type: T.boolean,
})
export type IUnderlineStyle = T.TypeOf<typeof UnderlineStyle>

export const StrikethroughStyle = StyleProp.define('blaze:strikethrough', {
  defaultValue: false,
  type: T.boolean,
})
export type IStrikethroughStyle = T.TypeOf<typeof StrikethroughStyle>

export const UppercaseStyle = StyleProp.define('blaze:uppercase', {
  defaultValue: false,
  type: T.boolean,
})
export type IUppercaseStyle = T.TypeOf<typeof UppercaseStyle>

export const GeoStyle = StyleProp.defineEnum('blaze:geo', {
  defaultValue: 'rectangle',
  values: [
    'circle',
    'rectangle',
    'triangle',
    'diamond',
    'pentagon',
    'hexagon',
    'octagon',
    'parallelogram',
    'trapezoid',
    'halfCircle',
    'pill',
    'fourPointStar',
    'fivePointStarAlt',
    'fivePointStar',
    'sixPointStar',
    'eightPointStar',
    'starBurst',
    'promoCircle',
    'plus',
    'arrowLeft',
    'arrowRight',
    'arrowUp',
    'arrowDown',
    'arrowLeftRight',
    'crescent',
    'splash',
    'heart',
    'drop',
    'cloud',
    'bookmark',
    'pointer',
    'ticket',
  ],
})
export type IGeoStyle = T.TypeOf<typeof GeoStyle>

export const StrokeColorStyle = StyleProp.define('blaze:strokeColor', {
  defaultValue: '#000000',
  type: T.string,
})
export type IStrokeColorStyle = T.TypeOf<typeof StrokeColorStyle>

export const MAX_STROKE_WIDTH = 100
export const StrokeWidthStyle = StyleProp.define('blaze:strokeWidth', {
  defaultValue: 0,
  type: T.positiveInteger.check((value) => {
    if (value > MAX_STROKE_WIDTH)
      throw new Error(`Expected a positive integer lower than ${MAX_STROKE_WIDTH}, got ${value}`)
  }),
})
export type IStrokeWidthStyle = T.TypeOf<typeof StrokeWidthStyle>

export const CornerRoundingStyle = StyleProp.define('blaze:cornerRounding', {
  defaultValue: 0,
  type: T.positiveInteger,
})
export type ICornerRoundingStyle = T.TypeOf<typeof CornerRoundingStyle>

export const LineHeightStyle = StyleProp.define('blaze:lineHeight', {
  defaultValue: 1.4,
  type: T.number,
})
export type ILineHeightStyle = T.TypeOf<typeof LineHeightStyle>

export const LetterSpacingStyle = StyleProp.define('blaze:letterSpacing', {
  defaultValue: 0,
  type: T.number,
})
export type ILetterSpacingStyle = T.TypeOf<typeof LetterSpacingStyle>

export const ShadowStyle = StyleProp.define('blaze:shadow', {
  defaultValue: null,
  type: T.object({
    x: T.number,
    y: T.number,
    blur: T.positiveNumber,
    color: T.string,
    opacity: T.positiveNumber.check(normalizedPercentage),
  }).nullable(),
})
export type IShadowStyle = T.TypeOf<typeof ShadowStyle>

const SolidColorValidator = T.object({
  type: T.literal('solidColor'),
  color: T.string,
  opacity: T.positiveNumber.check(normalizedPercentage),
})

const LinearGradientValidator = T.object({
  type: T.literal('linearGradient'),
  angle: T.number,
  stops: T.arrayOf(
    T.object({
      color: T.string,
      opacity: T.positiveNumber.check(normalizedPercentage),
      position: T.positiveNumber.check(normalizedPercentage),
    }),
  ),
})

export const FillStyle = StyleProp.define('blaze:fill', {
  defaultValue: { type: 'solidColor', color: '#000000', opacity: 1 },
  type: T.union('type', { solidColor: SolidColorValidator, linearGradient: LinearGradientValidator }),
})
export type IFillStyle = T.TypeOf<typeof FillStyle>

export const RemoveBackgroundStyle = StyleProp.define('blaze:cutout', {
  defaultValue: null,
  type: T.object({
    crop: T.boolean,
  }).nullable(),
})
export type IRemoveBackgroundStyle = T.TypeOf<typeof RemoveBackgroundStyle>

export const VerticalAlignStyle = StyleProp.defineEnum('blaze:verticalAlign', {
  defaultValue: 'middle',
  values: ['top', 'middle', 'bottom'],
})
export type IVerticalAlignStyle = T.TypeOf<typeof VerticalAlignStyle>

export const TextBoxBackgroundFillStyle = StyleProp.define('blaze:textBoxBackgroundFill', {
  defaultValue: null,
  type: T.union('type', { solidColor: SolidColorValidator, linearGradient: LinearGradientValidator }).nullable(),
})
export type ITextBoxBackgroundFillStyle = T.TypeOf<typeof TextBoxBackgroundFillStyle>

export const TextBoxBackgroundShapeStyle = StyleProp.defineEnum('blaze:textBoxBackgroundShape', {
  defaultValue: 'box',
  values: ['box', 'silhouetteSquare', 'silhouetteRounded', 'callToAction'],
})
export type ITextBoxBackgroundShapeStyle = T.TypeOf<typeof TextBoxBackgroundShapeStyle>
